import { useCallback, useMemo, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Ban, ChevronDown, CircleCheck, Search } from 'lucide-react';
import { SecurityItemRow } from './SecurityItemRow';
import { FilterCombobox } from '../../FilterCombobox';
import type { VirtualItem } from '@tanstack/react-virtual';
import type { Security } from '@biasafe.models';
import {
  STATIC_SECTORS,
  securityTypeLabel,
} from '../../../config/constants/staticClassifications';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface SecuritySearchPopoverProps {
  variant: 'blocked' | 'allowed';
  items: Array<Security>;
  onSelect: (symbol: string) => void;
}

// Matches the fixed h-12 (48px) rows rendered by SecurityItemRow — the
// virtualizer positions rows at multiples of this, so it must equal the row's
// real height or rows would overlap / gap.
const ROW_HEIGHT = 48;

// Morningstar sector code (Security.sectorCode, e.g. "311") -> display name
// ("Technology"). Built once from the static classification union so each row's
// chip shows the sector NAME the Figma design uses, not the raw numeric code.
const SECTOR_LABEL = new Map(
  STATIC_SECTORS.map((s) => [s.code, s.label] as const),
);

// ISO-2 country code -> English display name (same source the country filter in
// useCountriesFromSecurities uses).
const REGION_NAMES = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();
const countryName = (code: string): string => {
  try {
    return REGION_NAMES?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
};

// Copy is hardcoded from Figma UniverseSelection.tsx line 5502 — same
// trigger label whether the popover is in blocked or allowed mode,
// only the keyword and tint change. The earlier i18n indirection was
// driving the "Search to exclude..." label out of sync between Figma
// updates and the locale file; hardcoding is the design owner's
// chosen path on this branch.
export function SecuritySearchPopover({
  variant,
  items,
  onSelect,
}: SecuritySearchPopoverProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Figma filter bar — reuses FilterCombobox (the index picker's control).
  const [countryFilter, setCountryFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sectorFilter, setSectorFilter] = useState('All');

  const isBlocked = variant === 'blocked';
  const Icon = isBlocked ? Ban : CircleCheck;
  const iconColor = isBlocked ? 'text-destructive' : 'text-green-600';
  const keyword = isBlocked ? 'exclude' : 'include';
  // Figma 5502 renders "exclude" in destructive and "include" in
  // green — matched to the Ban / CircleCheck glyph tint.
  const keywordColor = isBlocked
    ? 'text-destructive font-medium'
    : 'text-green-600 font-medium';
  const searchPlaceholder = isBlocked
    ? 'Search stocks to blacklist...'
    : 'Search stocks to allow...';

  // Distinct filter options from the (already universe-scoped) items, so the
  // dropdowns only ever offer values that exist in the selected index.
  const countryOptions = useMemo(
    () =>
      [...new Set(items.map((s) => s.country).filter(Boolean))].sort((a, b) =>
        countryName(a).localeCompare(countryName(b)),
      ),
    [items],
  );
  const typeOptions = useMemo(
    () => [...new Set(items.map((s) => s.securityType).filter(Boolean))].sort(),
    [items],
  );
  const sectorOptions = useMemo(
    () =>
      [
        ...new Set(
          items.map((s) => SECTOR_LABEL.get(s.sectorCode) ?? s.sectorCode),
        ),
      ]
        .filter(Boolean)
        .sort(),
    [items],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = items.filter((item) => {
      if (countryFilter !== 'All' && item.country !== countryFilter)
        return false;
      if (typeFilter !== 'All' && item.securityType !== typeFilter)
        return false;
      const sectorLabel = SECTOR_LABEL.get(item.sectorCode) ?? item.sectorCode;
      if (sectorFilter !== 'All' && sectorLabel !== sectorFilter) return false;
      if (!q) return true;
      return (
        item.ticker.toLowerCase().includes(q) ||
        item.name.toLowerCase().includes(q) ||
        item.sectorCode.toLowerCase().includes(q) ||
        sectorLabel.toLowerCase().includes(q) ||
        (item.mic?.toLowerCase().includes(q) ?? false) ||
        item.country.toLowerCase().includes(q)
      );
    });
    // Rank by largest market cap. The requested secondary key — the latest
    // date each name appeared in the index — is NOT in the security-items
    // payload (that's point-in-time constituent data), so ties fall back to
    // ticker for a stable order until the backend exposes an appearance date.
    return matches.sort(
      (a, b) =>
        (b.marketCap ?? 0) - (a.marketCap ?? 0) ||
        a.ticker.localeCompare(b.ticker),
    );
  }, [items, search, countryFilter, typeFilter, sectorFilter]);

  // Use state-based ref so the virtualizer re-initializes when the
  // scroll container mounts (e.g. when the popover opens).
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const handleSelect = useCallback(
    (symbol: string) => {
      onSelect(symbol);
      setSearch('');
      setOpen(false);
    },
    [onSelect],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Figma 5493-5505: outline combobox, h-10, Ban+exclude-red label,
            trailing chevron. Icon / keyword / tint stay variant-driven so the
            allowed (CircleCheck/green) path keeps working. */}
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-10 text-left"
        >
          <div className="flex items-center gap-2">
            <Icon
              strokeWidth={isBlocked ? 3 : 2}
              className={`w-3 h-3 ${iconColor}`}
            />
            {/* Figma 5502 copy verbatim. */}
            <span className="text-muted-foreground text-[15px]">
              Search tickers or companies to{' '}
              <span className={keywordColor}>{keyword}</span>{' '}
              regardless of selected screens...
            </span>
          </div>
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        side="bottom"
        sideOffset={4}
      >
        {/* Figma 5508-5700: Command shell — CommandInput over a CommandList
            capped at max-h-[400px]. The hand-rolled virtualized list stands in
            for cmdk so large security catalogs stay performant; only the
            chrome is matched to the Command primitive. */}
        <div className="flex flex-col bg-popover text-popover-foreground rounded-md overflow-hidden">
          {/* Figma 5509 CommandInput: border-b wrapper, muted search glyph. */}
          <div className="flex items-center gap-2 border-b px-3 h-9">
            <Search className="size-4 shrink-0 opacity-50" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          {/* One scroll region holds the sticky frosted filter bar AND the
              list, so rows scroll UNDER the bar — the backdrop-blur needs
              content behind it to frost, exactly like the index picker's
              CommandList. */}
          <div ref={setScrollEl} className="max-h-[400px] overflow-y-auto">
            {/* Figma filter bar (All countries / types / sectors), shared
                FilterCombobox — sticky + frosted like the index picker. */}
            {items.length > 0 && (
              <div className="sticky top-0 z-10 flex items-center gap-4 border-b border-border/50 bg-background/70 px-3 py-2 backdrop-blur-lg">
                <FilterCombobox
                  allLabel="All countries"
                  value={countryFilter}
                  options={countryOptions}
                  onChange={setCountryFilter}
                  renderOption={countryName}
                />
                <FilterCombobox
                  allLabel="All types"
                  value={typeFilter}
                  options={typeOptions}
                  onChange={setTypeFilter}
                  renderOption={securityTypeLabel}
                />
                <FilterCombobox
                  allLabel="All sectors"
                  value={sectorFilter}
                  options={sectorOptions}
                  onChange={setSectorFilter}
                />
              </div>
            )}

            {filtered.length === 0 ? (
              // When the universe-security-items API hasn't returned yet OR the
              // user typed a ticker that isn't in our catalog (e.g. a
              // freshly-IPO'd name), let them add it as a manual exclusion. The
              // backend treats the symbol as a plain string in the blocked-list
              // payload, so anything the user types lands in the strategy
              // config; the worker ignores unknown tickers at backtest time.
              <div className="p-1">
                {search.trim() ? (
                  <button
                    type="button"
                    onClick={() => handleSelect(search.trim().toUpperCase())}
                    className="flex items-center gap-2 w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    <Icon
                      strokeWidth={isBlocked ? 3 : 2}
                      className={`w-4 h-4 ${iconColor} flex-shrink-0`}
                    />
                    <span className="flex-1">
                      {isBlocked ? 'Exclude' : 'Include'}{' '}
                      <span className="font-medium">
                        {search.trim().toUpperCase()}
                      </span>{' '}
                      <span className="text-muted-foreground text-xs">
                        (custom ticker)
                      </span>
                    </span>
                  </button>
                ) : (
                  // Figma 5511 CommandEmpty: py-6 text-center text-sm.
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    Type a ticker or company name to {keyword}.
                  </p>
                )}
              </div>
            ) : (
              <div
                style={{
                  height: virtualizer.getTotalSize(),
                  position: 'relative',
                }}
              >
                {virtualizer
                  .getVirtualItems()
                  .map((virtualRow: VirtualItem) => {
                    const item = filtered[virtualRow.index];
                    return (
                      <div
                        key={virtualRow.key}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: virtualRow.size,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        <SecurityItemRow
                          symbol={item.ticker}
                          name={item.name}
                          sectorLabel={
                            SECTOR_LABEL.get(item.sectorCode) ??
                            item.sectorCode
                          }
                          country={item.country}
                          mic={item.mic}
                          // Store the engine's performance_id (ticker only for a
                          // custom/unknown entry); the row still shows the ticker.
                          onClick={() =>
                            handleSelect(item.performanceId ?? item.ticker)
                          }
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
